import { toast } from '@buffer-mono/popcorn'

import type { CustomTemplateDialogTemplate } from '~publish/components/CustomTemplateDialog'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { DeletePostTemplateMutation } from '../graphql/postTemplates'
import { evictMyPostTemplate } from './customTemplateCache'
import type { CustomTemplate } from './useMyPostTemplates'

import type { Template } from '../types'

export const toDialogTemplate = (
  t: CustomTemplate,
): CustomTemplateDialogTemplate => ({
  id: t.id,
  title: t.title,
  body: t.body,
  description: t.description,
  emoji: t.emoji,
  visibility: t.visibility,
})

export const isCustomTemplate = (
  template: Template | CustomTemplate,
): template is CustomTemplate => 'owner' in template

// Duplicating a Buffer-curated template into the actor's own library should
// not carry over campaign linkage or curated metadata — those belong to the
// public template, not to the user's copy.
export const toCustomFromBuffer = (template: Template): CustomTemplate => ({
  ...template,
  tags: [],
  linkedCampaigns: [],
  featured: false,
  owner: 'mine',
  visibility: 'private',
  allowedActions: ['view', 'edit', 'delete'],
})

type UseDeleteCustomTemplateResult = {
  deleteTemplate: (id: string) => Promise<void>
  deleting: boolean
}

export function useDeleteCustomTemplate(): UseDeleteCustomTemplateResult {
  const [mutate, { loading }] = useTypedMutation(
    DeletePostTemplateMutation,
    (data) => data.deletePostTemplate,
    {
      successTypename: 'EmptySuccess',
      update: (cache, { data }, options) => {
        if (data?.deletePostTemplate?.__typename !== 'EmptySuccess') return
        const id = options.variables?.input?.id
        if (id) evictMyPostTemplate(cache, id)
      },
    },
  )

  const deleteTemplate = async (id: string): Promise<void> => {
    const result = await mutate({ variables: { input: { id } } })
    if (result.success) {
      toast.success('Template deleted')
    } else {
      toast.error('Could not delete template. Please try again.')
    }
  }

  return { deleteTemplate, deleting: loading }
}
